/* eslint-disable no-console */
'use client';
import type {
  AttributesEntity,
  ValuesEntity,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import ProductVariantCheckbox from '@eliadmin/core/src/components/product/detail/variants/product-variant-checkbox';
import ProductVariantColor from '@eliadmin/core/src/components/product/detail/variants/product-variant-color';
import ProductVariantRadio from '@eliadmin/core/src/components/product/detail/variants/product-variant-radio';
import { ProductVariantSelect } from '@eliadmin/core/src/components/product/detail/variants/product-variant-select';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';
import {
  sortVariantsByAvailability,
  useVariantsStore,
} from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect } from 'react';

export type ProductVariantsProps = { attributes: AttributesEntity[]; variants: VariantsEntity[] };

export function ProductVariants() {
  const attributes = useVariantsStore(state => state.product.attributes) || [];
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const setSelectedVariant = useVariantsStore(s => s.setSelectedVariant);
  const updateSelectedValue = useVariantsStore(s => s.updateSelectedValue);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const variants = useVariantsStore(state => state.product.variants) || [];

  useEffect(() => {
    /** select first available variant if nothing is selected on mount */
    /** a lot of nullchecking to make sure it wont crash some overriden projects*/
    try {
      if (selectedVariant) return;
      if (!Array.isArray(variants) || variants.length === 0) return;

      const sorted = sortVariantsByAvailability(variants) || [];
      const firstVariant = sorted[0];
      if (!firstVariant) return;

      const values = Array.isArray(firstVariant.values) ? firstVariant.values : [];
      values.forEach(value => {
        if (!value) return;
        const attribute = attributes?.find(a => a && a.id === value.attribute_id);
        if (!attribute) return;
        if (selectedValues.some(selectedValue => selectedValue.value.id === attribute.id)) return;
        updateSelectedValue({
          attribute,
          value: value as ValuesEntity,
        });
      });
      setSelectedVariant(firstVariant);
    } catch (e) {
      console.warn('Failed to initialize default variant', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectableAttributes = attributes.filter(
    attribute => attribute.type !== AttributeType.RANGE && (attribute.values?.length ?? 0) > 1,
  );

  if (selectableAttributes.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col space-y-5', 'ea-product-detail-variants')}>
      {selectableAttributes.map((attribute, i) => {
        if (!Object.values(AttributeType).includes(attribute.type as AttributeType)) {
          console.warn(`Unknown attribute type: ${attribute.type} with id: ${attribute.id}`);
          return null;
        }

        if (attribute.type === AttributeType.RANGE) {
          return null;
        }

        return (
          <div
            key={`attributes-${attribute.id}`}
            className={cn('flex flex-col gap-2', 'ea-product-detail-variants__attribute')}
          >
            <span className="text-xs font-semibold">
              <strong>{attribute.name}</strong>
            </span>
            {attribute.type === AttributeType.COLOR && (
              <ProductVariantColor key={`attribute-${i}`} attribute={attribute} />
            )}
            {(attribute.type === AttributeType.RADIO ||
              attribute.type === AttributeType.SLIDER_RADIO) && (
              <ProductVariantRadio key={`attribute-${i}`} attribute={attribute} />
            )}
            {(attribute.type === AttributeType.SELECT ||
              attribute.type === AttributeType.SLIDER_SELECT) && (
              <ProductVariantSelect key={`attribute-${i}`} attribute={attribute} />
            )}
            {attribute.type === AttributeType.CHECKBOX && (
              <ProductVariantCheckbox key={`attribute-${i}`} attribute={attribute} />
            )}
          </div>
        );
      })}
    </div>
  );
}
